import React from "react";
import { ArrowRight, Play, ShieldCheck } from "lucide-react";

export const Hero = ({ onJoinClick, onWatchDemo }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Background image with heavy overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg"
          alt="Electrician working on circuit breaker panel"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-grid opacity-40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 w-full">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <div className="lg:col-span-7 ag-fade-up">
            <div className="inline-flex items-center gap-2 border border-[#27272A] bg-[#141414]/80 backdrop-blur-sm px-3 py-1.5 rounded-sm mb-7">
              <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full animate-pulse" />
              <span className="font-mono-spec text-[11px] uppercase tracking-[0.22em] text-[#D4D4D8]">
                Patent Pending · UK Engineered
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.4rem] leading-[0.95] tracking-tighter"
            >
              Precision SWA{" "}
              <span className="text-[#FF6B00]">Gland Marking</span>.
              <br />
              Every Time.
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-7 text-[#A1A1AA] text-base md:text-lg max-w-xl leading-relaxed"
            >
              A patent-pending professional tool engineered to help electricians
              mark cable gland positions faster, more accurately and more
              consistently — on every panel, every enclosure, every job.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                data-testid="hero-join-waitlist-btn"
                onClick={onJoinClick}
                className="btn-primary group"
              >
                Join the Waiting List
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-watch-demo-btn"
                onClick={onWatchDemo}
                className="btn-secondary"
              >
                <Play className="w-4 h-4" /> Watch Product Demo
              </button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-lg">
              {[
                { v: "10×", l: "Faster Marking" },
                { v: "±0.5mm", l: "Repeatable" },
                { v: "100%", l: "Trade Built" },
              ].map((s) => (
                <div key={s.l} className="border-l border-[#27272A] pl-3 md:pl-4">
                  <div className="font-display text-2xl md:text-3xl text-white">
                    {s.v}
                  </div>
                  <div className="font-mono-spec text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Product placeholder card */}
          <div className="lg:col-span-5 relative ag-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative aspect-square max-w-md mx-auto">
              <div className="absolute inset-0 bg-gradient-radial from-[#FF6B00]/20 via-transparent to-transparent blur-3xl" />
              <div className="relative h-full bg-gradient-to-br from-[#1F1F1F] to-[#0A0A0A] border border-[#27272A] rounded-sm metal-edge overflow-hidden">
                <img
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_gland-positioning/artifacts/312f969b062873e7_IMG_2308.png"
                  alt="AdjustaGland PRO — hand-held precision tool with scale markings"
                  className="absolute inset-0 w-full h-full object-contain p-4 ag-float"
                />
              </div>
            </div>

            {/* Caption below image */}
            <div className="max-w-md mx-auto mt-5 flex items-center justify-between gap-4">
              <div>
                <div className="font-display text-xl md:text-2xl">AdjustaGland PRO</div>
                <div className="font-mono-spec text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] mt-1">
                  Model · AG-PRO-01 · 20mm · 25mm · 32mm
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#FF6B00]" />
                <span className="font-mono-spec text-[10px] uppercase tracking-[0.2em] text-[#FF6B00] font-bold">
                  Patent Pending
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-4 flex flex-wrap items-center justify-between gap-4">
          <span className="font-mono-spec text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA]">
            Designed For · Commercial Electricians · Panel Builders · Switchgear OEMs
          </span>
          <span className="font-mono-spec text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA]">
            UK Engineered · Pre-Launch 2026
          </span>
        </div>
      </div>
    </section>
  );
};
